import { skipToken, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ActivitiesFeed } from './activities-feed';
import { ComposerStateProvider } from './composer-state';
import {
  ContactInfoPanel,
  ContactInfoPanelSkeleton,
} from './contact-info-panel';
import {
  OpenTaskPill,
  TaskMessageComposer,
  getInitialTaskComposerView,
} from './task-message-panel';
import type { ComposerReplyView } from './composer-state';
import type { TaskMessageComposerProps } from './task-message-panel';
import type { ContactDetails } from './types';
import { useTRPC } from '~/lib/trpc';

type ContactDetailsPanelProps = {
  contact: ContactDetails | undefined;
};

export function ContactDetailsPanel({ contact }: ContactDetailsPanelProps) {
  const isPending = contact === undefined;

  if (contact === null) {
    return <NoTaskSelectedPanel />;
  }

  return (
    <div className="flex h-full min-w-0 flex-1 overflow-hidden bg-background">
      <div className="min-w-0 flex-1">
        {contact && <ContactComposerPanels contact={contact} />}
      </div>
      {contact && <ContactInfoPanel contact={contact} />}
      {isPending && <ContactInfoPanelSkeleton />}
    </div>
  );
}

function ContactComposerPanels({
  contact,
}: {
  contact: NonNullable<ContactDetails>;
}) {
  const trpc = useTRPC();
  const { data: task } = useSuspenseQuery(
    trpc.tasks.management.getOpenForContact.queryOptions({
      contactId: contact.id,
    }),
  );
  const { data: emailProvider } = useSuspenseQuery(
    trpc.config.queries.getEmailProviderKind.queryOptions(),
  );
  const isMailbox = emailProvider === 'emailEngine';
  /** Only the deployment's own mailbox is asked: the other has nothing. */
  const emailReplyTarget = useQuery(
    trpc.activities.feed.getLastEmailReplyTarget.queryOptions(
      isMailbox ? skipToken : { contactId: contact.id },
    ),
  );
  const mailboxReplyTarget = useQuery(
    trpc.activities.feed.getLastMailboxReplyTarget.queryOptions(
      isMailbox ? { contactId: contact.id } : skipToken,
    ),
  );
  const isLastReplyTargetPending = isMailbox
    ? mailboxReplyTarget.isPending
    : emailReplyTarget.isPending;
  const lastReply: ComposerReplyView | null = isMailbox
    ? mailboxReplyTarget.data
      ? { view: 'mail', mode: 'reply', target: mailboxReplyTarget.data }
      : null
    : emailReplyTarget.data
      ? { view: 'email', mode: 'reply', target: emailReplyTarget.data }
      : null;
  const isInitializationPending = Boolean(task) && isLastReplyTargetPending;
  const initialState = isInitializationPending
    ? { view: 'collapsed' as const }
    : getInitialTaskComposerView({
        task,
        email: contact.emails.at(0),
        phoneNumber: contact.phoneNumbers.at(0),
        emailProvider,
        lastReply,
      });
  const providerKey = `${contact.id}:${task?.id ?? 'none'}:${
    isInitializationPending ? 'pending' : 'ready'
  }`;

  return (
    <ComposerStateProvider key={providerKey} initialState={initialState}>
      <ContactActivityPanels
        contact={contact}
        task={task}
        emailProvider={emailProvider}
        lastReply={lastReply}
        isLastReplyTargetPending={isLastReplyTargetPending}
      />
    </ComposerStateProvider>
  );
}

function NoTaskSelectedPanel() {
  const { t } = useTranslation('tasks');

  return (
    <div className="flex h-full min-w-0 flex-1 items-center justify-center bg-background px-6 text-center">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-foreground">
          {t('No task selected')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('Select a task from the list')}
        </p>
      </div>
    </div>
  );
}

function ContactActivityPanels({
  contact,
  task,
  emailProvider,
  lastReply,
  isLastReplyTargetPending,
}: {
  contact: NonNullable<ContactDetails>;
  task: TaskMessageComposerProps['task'];
  emailProvider: TaskMessageComposerProps['emailProvider'];
  lastReply: TaskMessageComposerProps['lastReply'];
  isLastReplyTargetPending: boolean;
}) {
  /** The composer takes the height it needs and the feed takes the rest —
   *  rather than a percentage split, where every drag took height off the feed. */
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1">
          <ActivitiesFeed
            contactId={contact.id}
            contactName={contact.displayName}
          />
        </div>
        <OpenTaskPill contactId={contact.id} />
      </div>
      <div className="shrink-0">
        <TaskMessageComposer
          contactId={contact.id}
          email={contact.emails.at(0)}
          phoneNumber={contact.phoneNumbers.at(0)}
          task={task}
          emailProvider={emailProvider}
          lastReply={lastReply}
          isLastReplyTargetPending={isLastReplyTargetPending}
        />
      </div>
    </div>
  );
}

export function ContactDetailsPanelPending() {
  return <ContactDetailsPanel contact={undefined} />;
}

export function ContactNotFoundPanel() {
  const { t } = useTranslation('tasks');

  return (
    <div className="flex h-full min-w-0 flex-1 items-center justify-center bg-background px-6 text-center">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-foreground">
          {t('Task not found')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('Select another task from the list')}
        </p>
      </div>
    </div>
  );
}
